import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { getProfileAssetUrl } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Plus, Search, Edit, Trash2, Users, Mail, Phone, Briefcase } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import MobileInput from './ui/mobile-input';
import DateInput from './ui/date-input';
import { validateMobile } from '../utils/phoneValidation';
import { TEACHER_DESIGNATIONS } from '../utils/teacherDesignations';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';

const Teachers = () => {
  const { user } = useAuth();
  const { showBranchLabels } = useBranchLabels();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [nextEmployeeIdLoading, setNextEmployeeIdLoading] = useState(false);
  const [nextEmployeeIdError, setNextEmployeeIdError] = useState('');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', employee_id: '',
    designation: '', department: '', qualification: '', experience_years: '',
    date_of_birth: '', gender: '', joining_date: '', salary: '',
  });

  useEffect(() => { fetchTeachers(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchTeachers();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  useEffect(() => {
    if (!isDialogOpen) return;
    let cancelled = false;
    const loadNext = async () => {
      setNextEmployeeIdLoading(true);
      setNextEmployeeIdError('');
      try {
        const schoolId = user?.school_id;
        const res = await api.get('/teachers/next-employee-id', {
          params: schoolId ? { school_id: schoolId } : {},
        });
        const eid = res.data?.data?.employee_id;
        if (cancelled) return;
        if (eid) {
          setFormData((prev) => ({ ...prev, employee_id: eid }));
        } else {
          setNextEmployeeIdError('No employee ID returned');
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err.response?.data?.message || 'Could not load next employee ID';
        setNextEmployeeIdError(msg);
        setFormData((prev) => ({ ...prev, employee_id: '' }));
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      } finally {
        if (!cancelled) setNextEmployeeIdLoading(false);
      }
    };
    loadNext();
    return () => { cancelled = true; };
  }, [isDialogOpen, user?.school_id]);

  const fetchTeachers = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get('/teachers', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      setTeachers(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch teachers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateMobile(formData.phone);
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    try {
      const payload = { ...formData, phone: formData.phone || null };
      if (editingTeacher) {
        await api.put(`/teachers/${editingTeacher.id}`, payload);
        toast({ title: 'Success', description: 'Teacher updated successfully' });
      } else {
        await api.post('/teachers', payload);
        toast({ title: 'Success', description: 'Teacher created. Credentials sent via email.' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTeachers();
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || 'Failed';
      if (status === 409) {
        try {
          const schoolId = user?.school_id;
          const res = await api.get('/teachers/next-employee-id', {
            params: schoolId ? { school_id: schoolId } : {},
          });
          const eid = res.data?.data?.employee_id;
          if (eid) setFormData((prev) => ({ ...prev, employee_id: eid }));
        } catch (_) { /* ignore */ }
      }
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleEdit = (teacher) => {
    navigate(`/hr/teachers/${teacher.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this teacher?')) {
      try {
        await api.delete(`/teachers/${id}`);
        toast({ title: 'Success', description: 'Teacher deleted' });
        fetchTeachers();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', employee_id: '',
      designation: '', department: '', qualification: '', experience_years: '',
      date_of_birth: '', gender: '', joining_date: '', salary: '' });
    setEditingTeacher(null);
  };

  const filtered = teachers.filter(t => 
    `${t.first_name} ${t.last_name} ${t.employee_id} ${t.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Teachers</h1>
          <p className="text-muted-foreground">Manage teaching staff</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
            setNextEmployeeIdError('');
            setNextEmployeeIdLoading(false);
          }
        }}
        >
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" data-testid="add-teacher-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Teacher</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className="bg-muted border-border" required /></div>
                <div><Label>Last Name</Label><Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className="bg-muted border-border" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-muted border-border" required /></div>
                <div><Label>Phone</Label><MobileInput value={formData.phone} onChange={(v) => { setFormData({ ...formData, phone: v }); setPhoneError(''); }} error={phoneError} className="bg-muted border-border" /></div>
              </div>
              <div>
                <Label>Employee ID</Label>
                <Input
                  value={nextEmployeeIdLoading ? '…' : formData.employee_id}
                  readOnly
                  disabled
                  className="bg-muted/80 border-border text-foreground font-mono mt-1 cursor-not-allowed opacity-90"
                  aria-busy={nextEmployeeIdLoading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-assigned from school short name (Settings). Sent with create request.
                </p>
                {nextEmployeeIdError && (
                  <p className="text-xs text-red-400 mt-1">{nextEmployeeIdError}</p>
                )}
              </div>
              <div>
                <Label>Designation</Label>
                <Select value={formData.designation} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
                  <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {TEACHER_DESIGNATIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="bg-muted border-border" /></div>
                <div><Label>Qualification</Label><Input value={formData.qualification} onChange={(e) => setFormData({...formData, qualification: e.target.value})} className="bg-muted border-border" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Joining Date</Label><DateInput value={formData.joining_date} onChange={(v) => setFormData({...formData, joining_date: v})} className="bg-muted border-border" /></div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={nextEmployeeIdLoading || !formData.employee_id || !!nextEmployeeIdError}
              >
                Create
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input type="search" placeholder="Search teachers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-muted border-border text-foreground" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="border-border"><CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No teachers found</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map((teacher) => (
            <Card key={teacher.id} className="border-border hover:border-border transition-colors">
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Link
                    to={`/hr/teachers/${teacher.id}`}
                    className="flex items-start gap-3 flex-1 min-w-0"
                  >
                    <Avatar className="w-12 h-12 shrink-0">
                      <AvatarImage
                        src={getProfileAssetUrl(teacher.avatar)}
                        alt={`${teacher.first_name} ${teacher.last_name}`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-green-500/20 text-green-400 font-semibold">
                        {teacher.first_name?.[0]?.toUpperCase()}{teacher.last_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-foreground truncate hover:text-green-300 transition-colors flex items-center gap-2 flex-wrap">
                        {teacher.first_name} {teacher.last_name}
                        <BranchLabel name={teacher.branch_name} show={showBranchLabels} />
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">{teacher.employee_id} • {teacher.designation || 'Teacher'}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="text-xs text-muted-foreground flex items-center gap-1 min-w-0 max-w-full truncate"><Mail className="w-3 h-3 shrink-0" /> {teacher.email}</span>
                        {teacher.department && <span className="text-xs text-muted-foreground flex items-center gap-1"><Briefcase className="w-3 h-3 shrink-0" /> {teacher.department}</span>}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); handleEdit(teacher); }} className="text-muted-foreground hover:text-foreground"><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.preventDefault(); handleDelete(teacher.id); }} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Teachers;
